import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  DataCategoryState,
  DataCategoryResponse,
  DataCategory,
} from "./types/data-types";
import { PaginationReturn } from "./types/types";

const initialState: DataCategoryState = {
  dataCategories: [],
  dataCategory: null,
  selectedDataCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataCategory (supports FormData for image upload)
export const createDataCategory = createAsyncThunk<
  DataCategoryResponse,
  FormData | Omit<DataCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("dataCategories/create", async (dataCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dataCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/data-categories`,
      dataCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data category" },
    );
  }
});

// READ (fetch all) DataCategories (with pagination)
export const fetchDataCategories = createAsyncThunk<
  DataCategoryResponse & PaginationReturn,
  void | { url?: string; perPage?: number; page?: number },
  { rejectValue: ApiError; state: RootState }
>("dataCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const perPage = urlFilters?.perPage ?? 1000;
    if (urlFilters?.url) {
      urlFilters.url = "";
    }
    const apiUrl = url
      ? url
      : `${API_URL}/data-categories?per_page=${encodeURIComponent(String(perPage))}`;
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      dataCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data categories" },
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchDataCategoryById = createAsyncThunk<
  DataCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/data-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data category" },
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateDataCategory = createAsyncThunk<
  DataCategory,
  { id: number; data: Partial<DataCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("dataCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/data-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data category" },
    );
  }
});

// DELETE DataCategory
export const deleteDataCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/data-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data category" },
    );
  }
});

// Slice
const dataCategorySlice = createSlice({
  name: "dataCategories",
  initialState,
  reducers: {
    resetDataCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedDataCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createDataCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDataCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dataCategory) {
          state.dataCategories.unshift(action.payload.dataCategory);
        }
      })
      .addCase(createDataCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create data category";
      })

      // Fetch All
      .addCase(fetchDataCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.dataCategories = action.payload.dataCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDataCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data categories";
      })

      // Fetch One
      .addCase(fetchDataCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDataCategory = null;
      })
      .addCase(fetchDataCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDataCategory = action.payload;
      })
      .addCase(fetchDataCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data category";
      })

      // Update
      .addCase(updateDataCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDataCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dataCategories = state.dataCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category,
        );
        if (state.selectedDataCategory?.id === action.payload.id) {
          state.selectedDataCategory = action.payload;
        }
      })
      .addCase(updateDataCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update data category";
      })

      // Delete
      .addCase(deleteDataCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDataCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dataCategories = state.dataCategories.filter(
          (category) => category.id !== action.payload,
        );
      })
      .addCase(deleteDataCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete data category";
      });
  },
});

export const { resetDataCategoryState } = dataCategorySlice.actions;
export default dataCategorySlice.reducer;
